import * as pdfjs from "pdfjs-dist";
// @ts-ignore
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

import type { TextItem as PdfjsTextItem } from "pdfjs-dist/types/src/display/api";

type ExtractedLink = {
  url: string;
  text: string;     // text recovered from intersecting text items (may be label or empty)
  page: number;
  rect?: number[];  // raw annotation rect
};

const URL_REGEX = /(https?:\/\/[^\s)>\]]+|www\.[^\s)>\]]+|linkedin\.com\/[^\s)>\]]+|github\.com\/[^\s)>\]]+)/gi;

export const readRawPdf = async (
  fileUrl: string
): Promise<{ text: string; links: ExtractedLink[] }> => {
  const pdfFile = await pdfjs.getDocument(fileUrl).promise;
  let fullText = "";
  const links: ExtractedLink[] = [];

  for (let pageNum = 1; pageNum <= pdfFile.numPages; pageNum++) {
    const page = await pdfFile.getPage(pageNum);
    const textContent = await page.getTextContent();
    const items = textContent.items as PdfjsTextItem[];

    // Build page text (safe)
    const pageText = items
      .map((it) => {
        const text = (it as PdfjsTextItem).str || "";
        return text.replace(/-­‐/g, "-"); // keep your hyphen fix
      })
      .join(" ");
    fullText += pageText + "\n";

    // Get annotations (await, not .then)
    const annots = await page.getAnnotations();

    // Precompute bbox for each text item (very small approximation)
    // Pdfjs transform is [a, b, c, d, e, f] — e/f are x/y for item baseline (approx)
    const textBoxes = items.map((it) => {
      const str = (it as PdfjsTextItem).str || "";
      const transform = (it as any).transform || [];
      // fallback positions when transform not present
      const x = transform.length >= 6 ? transform[4] : 0;
      const y = transform.length >= 6 ? transform[5] : 0;
      // approximate width: use width property or estimate by length
      const width = (it as any).width || Math.max(10, str.length * 5);
      const height = 10; // rough
      return { x, y, width, height, str };
    });

    // For each annotation, try to read URL-like fields and map to text
    for (const a of annots) {
      // Try multiple places where link may live
      const url =
        (a as any).url ||
        (a as any).action?.uri ||
        (a as any).A?.URI ||
        (a as any).dest ||
        null;

      // If annotation has a rect, normalize it: pdf.js rect = [x1, y1, x2, y2]
      const rect = (a as any).rect || null;

      // Collect candidate text under rect (if rect exists)
      let label = "";
      if (rect && rect.length === 4) {
        const [x1, y1, x2, y2] = rect;
        // pdf coordinate system might be bottom-left; we approximate by checking overlap
        const hits = textBoxes
          .filter((tb) => {
            // crude intersection test
            const tbx1 = tb.x;
            const tbx2 = tb.x + tb.width;
            const tby1 = tb.y - tb.height;
            const tby2 = tb.y;
            // check overlap with rect
            return !(tbx2 < Math.min(x1, x2) || tbx1 > Math.max(x1, x2) || tby2 < Math.min(y1, y2) || tby1 > Math.max(y1, y2));
          })
          .map((tb) => tb.str)
          .filter(Boolean);

        if (hits.length) {
          label = hits.join(" ").trim();
        }
      }

      // If url not present in annotation, try to find URLs in page text nearby
      if (!url) {
        // quick regex over pageText
        const rx = pageText.match(URL_REGEX);
        if (rx && rx.length) {
          for (const found of rx) {
            // avoid duplicates
            if (!links.some((l) => l.url === found)) {
              links.push({
                url: found,
                text: label || found,
                page: pageNum,
                rect: rect || undefined,
              });
            }
          }
          continue;
        } else {
          continue; // nothing to do
        }
      }

      // push url and attached label if found
      if (url && !links.some((l) => l.url === url)) {
        links.push({
          url,
          text: label || url,
          page: pageNum,
          rect: rect || undefined,
        });
      }
    } // end annots loop

    // Fallback: also add any plain-text URLs found by regex that aren't already included
    const rxAll = pageText.match(URL_REGEX);
    if (rxAll) {
      for (const found of rxAll) {
        if (!links.some((l) => l.url === found)) {
          links.push({
            url: found,
            text: found,
            page: pageNum,
          });
        }
      }
    }
  } // end pages loop

  return { text: fullText.trim(), links };
};